import { ComponentProps } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import * as client from "../../../client";
import {
  CatJobHistoryItem,
  CatResultsResponse,
} from "../../../client/types.gen";
import { AalDriversTab } from "./AalDriversTab";
import { EpCurvesTab } from "./EpCurvesTab";
import { LayerPerilTab } from "./LayerPerilTab";
import { RunHistoryTab } from "./RunHistoryTab";
import { SelectedModel } from "./types";
import styles from "./CatCard.module.css";

type CatResultsViewProps = {
  // On phones, render just the AAL-by-layer/peril table; on desktop, the full
  // tabbed results (EP Curves, AAL Drivers, Run History).
  isMobile: boolean;
  selectedModel: SelectedModel;
  rmsEltResultsData: CatResultsResponse | null;
  rms22ResultsData: CatResultsResponse | null;
  icmYltResultsData: CatResultsResponse | null;
  spatialKatResultsData: CatResultsResponse | null;
  layerInfoById: Map<string, client.Layer>;
  showAllLayers: boolean;
  onShowAllLayersChange: (value: boolean) => void;
  topLocationsByPeril:
    | ComponentProps<typeof AalDriversTab>["topLocationsByPeril"]
    | undefined;
  reportId: string;
  jobHistory: CatJobHistoryItem[];
  viewingBatchId: string | null;
  // Roof source that drives the per-peril "Enhanced · <source>" badge; null =
  // broker data.
  viewingEnhancedSource: string | null;
  onViewJobResults: (job: CatJobHistoryItem) => void;
  onDownloadAll: (job: CatJobHistoryItem) => void;
  onCompareJob: ComponentProps<typeof RunHistoryTab>["onCompareJob"];
  eltRecovery: ComponentProps<typeof RunHistoryTab>["eltRecovery"];
  activation: ComponentProps<typeof RunHistoryTab>["activation"];
};

export const CatResultsView = ({
  isMobile,
  selectedModel,
  rmsEltResultsData,
  rms22ResultsData,
  icmYltResultsData,
  spatialKatResultsData,
  layerInfoById,
  showAllLayers,
  onShowAllLayersChange,
  topLocationsByPeril,
  reportId,
  jobHistory,
  viewingBatchId,
  viewingEnhancedSource,
  onViewJobResults,
  onDownloadAll,
  onCompareJob,
  eltRecovery,
  activation,
}: CatResultsViewProps) => {
  const layerPerilTab = (renderToggle: boolean) => (
    <LayerPerilTab
      selectedModel={selectedModel}
      rmsEltResultsData={rmsEltResultsData}
      rms22ResultsData={rms22ResultsData}
      icmYltResultsData={icmYltResultsData}
      spatialKatResultsData={spatialKatResultsData}
      layerInfoById={layerInfoById}
      showAllLayers={showAllLayers}
      onShowAllLayersChange={onShowAllLayersChange}
      renderToggle={renderToggle}
      enhancedSource={viewingEnhancedSource}
    />
  );

  return (
    <div className={styles.resultsContainer}>
      {isMobile ? (
        layerPerilTab(false)
      ) : (
        <TabView>
          <TabPanel header="By Layer and Peril">{layerPerilTab(true)}</TabPanel>

          {selectedModel === "spatialkat" && (
            <TabPanel header="EP Curves">
              <EpCurvesTab
                selectedModel={selectedModel}
                spatialKatResultsData={spatialKatResultsData}
              />
            </TabPanel>
          )}

          {(selectedModel === "rms_elt" ||
            selectedModel === "rms_22" ||
            selectedModel === "icm_ylt") &&
            topLocationsByPeril &&
            Object.keys(topLocationsByPeril).length > 0 && (
              <TabPanel header="AAL Drivers">
                <AalDriversTab
                  topLocationsByPeril={topLocationsByPeril}
                  reportId={reportId}
                />
              </TabPanel>
            )}

          <TabPanel header="Run History">
            <RunHistoryTab
              jobHistory={jobHistory}
              selectedModel={selectedModel}
              viewingBatchId={viewingBatchId}
              onViewJobResults={onViewJobResults}
              onDownloadAll={onDownloadAll}
              onCompareJob={onCompareJob}
              eltRecovery={eltRecovery}
              activation={activation}
            />
          </TabPanel>
        </TabView>
      )}
    </div>
  );
};
